import { ActivityMonitorBoxProps } from "app/components/ActivityMonitor/types";
import Button from "app/storybookComponents/Button";
import Select from "react-select";
import NoResultsIcon from "resources/images/no-results-transparent-back.png";
import React from "react";
import { getSelectProps } from "utils/helperFunctions";
import { Dropdown, Form } from "react-bootstrap";
import AvatarCircle from "app/components/AvatarCircle";
import {
  getChangeSinceLast,
  getOutOfFiveColor,
  percentageColor,
} from "app/containers/Dashboard/helpers";
import { Team, UserInfo } from "app/containers/Global/types";
import { Department } from "app/containers/AdminConsole/types";
import { UserSubordinates } from "app/containers/UserSettings/types";
import {
  AllWeeklyCheckInTableData,
  CoachBoWeeklyCheckInActivityReport,
  isWeeklyCheckInRowUser,
  WeeklyCheckInCompanySettings,
  WeeklyCheckInCustomList,
  WeeklyCheckInLineChartResponse,
  WeeklyCheckInTableRowData,
  WeeklyCheckInRegradeData,
} from "./types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import { WEEKLY_CHECK_IN_FEATURE_NAME } from "./constants";
import SimplePopover from "app/components/Popovers/SimplePopover";

type BoolLike = boolean | 0 | 1;

export const getWeeklyCheckInTableRowEntityId = (
  data: WeeklyCheckInTableRowData
) => ("userAccountId" in data ? data.userAccountId : data.entityId);

export const getDateForSorting = (row: WeeklyCheckInTableRowData): Date => {
  if ("completedDate" in row) {
    return new Date(row.completedDate);
  } else if ("startDate" in row) {
    return new Date(row.startDate);
  }
  // Default to a very early date if no date is available
  return new Date(0);
};

export const sortWeeklyCheckInTableRowData = (
  rows: WeeklyCheckInTableRowData[]
) => {
  return rows.sort((a, b) => {
    const dateA = getDateForSorting(a);
    const dateB = getDateForSorting(b);

    // Sort by date in descending order
    return dateB.getTime() - dateA.getTime();
  });
};

export const updateUserWeeklyCheckInTableData = (
  array: WeeklyCheckInTableRowData[],
  updatedData: WeeklyCheckInRegradeData,
  threadId: string
) =>
  array.map((entry) => {
    if (isWeeklyCheckInRowUser(entry) && entry.threadId === threadId) {
      return {
        ...entry,
        ...updatedData,
      };
    }
    return entry;
  });

export const getWeeklyCheckInActivityTimeIntervalKey = (
  selectedKey?: string
) => {
  switch (selectedKey) {
    case "Past 30 days":
      return "last30Days";
    case "Past 365 days":
      return "last365Days";
    default:
      return null;
  }
};

export const getWeeklyCheckinActivityLineChartData = (
  weeklyCheckInLineChartResponse?: WeeklyCheckInLineChartResponse | null
): {
  dataValues: (number | null)[][];
  labels: string[];
  hasAtLeastTwoDataPoints: boolean;
} | null => {
  if (!weeklyCheckInLineChartResponse) {
    return null;
  }
  const {
    labels,
    averageEnergy = [],
    averageProgress = [],
    averageReadiness = [],
  } = weeklyCheckInLineChartResponse;
  const dataValues: (number | null)[][] = [];
  dataValues.push(averageEnergy, averageProgress, averageReadiness);

  const hasAtLeastTwoDataPoints =
    averageEnergy.filter((value) => value !== null).length > 1;

  return {
    dataValues,
    labels,
    hasAtLeastTwoDataPoints,
  };
};

export const getWeeklyCheckinActivityLineEmptyState = (
  lineChartTimeInterval: string,
  notEnoughDataPoints?: boolean
) => {
  const title = `Chart Unavailable for ‘${lineChartTimeInterval}’`;
  let description =
    "Once there is comparison data, this chart will be shown here.";

  if (notEnoughDataPoints) {
    description =
      "There's not enough data in this time period to build a trend chart. Trend charts require data in at least 2 time periods. Try another time period.";
  }

  return (
    <div className="line-chart-empty-state">
      <img
        src={NoResultsIcon}
        alt="no results"
        className="line-chart-empty-state__illustration"
      />
      <div>
        <div className="line-chart-empty-state__title">
          <p>{title}</p>
        </div>
        <div className="line-chart-empty-state__description">
          <p>{description}</p>
        </div>
      </div>
    </div>
  );
};

export const getCoachBoWeeklyCheckInActivityReportToActivityMonitorBoxProps = (
  coachBoWeeklyCheckInActivityReport?: CoachBoWeeklyCheckInActivityReport,
  timeInterval: string = "week",
  showingOnlySubordinates?: boolean,
  isIndividualContributor?: boolean,
  isConservativeMode?: boolean
): (ActivityMonitorBoxProps | JSX.Element)[] => {
  const activityMonitorBoxProps: (ActivityMonitorBoxProps | JSX.Element)[] = [];
  if (!coachBoWeeklyCheckInActivityReport) return activityMonitorBoxProps;

  const { completionInfo, energy, progress, readiness } =
    coachBoWeeklyCheckInActivityReport;

  const hasSomeData =
    !!completionInfo.completedCount || !!completionInfo.totalCount;

  activityMonitorBoxProps.push({
    title: "Check-in Completion",
    mainValue: `${completionInfo.percentage}%`,
    changeSinceLast: getChangeSinceLast(
      completionInfo.trendAmount,
      completionInfo.trendDirection,
      true
    ),
    descriptionText: hasSomeData
      ? `${completionInfo.completedCount ?? 0} / ${completionInfo.totalCount}`
      : null,
    timeInterval,
    dotColor: percentageColor(completionInfo.percentage),
    popoverText: `This is the percentage of ${WEEKLY_CHECK_IN_FEATURE_NAME}s completed out of all those sent in ${
      showingOnlySubordinates ? "your reporting structure" : "this organization"
    }.`,
  });

  if (isConservativeMode) {
    // If in conservative mode, we only show the completion info box, so we add a spacer
    activityMonitorBoxProps.push(<div className="col-span-2" />);
    return activityMonitorBoxProps;
  }

  const averageScoreDescriptionText = hasSomeData ? "out of 5" : null;
  const titlePrefix = isIndividualContributor ? "Your" : "Average";

  activityMonitorBoxProps.push(
    {
      title: `${titlePrefix} Mood Score`,
      mainValue: energy.score,
      changeSinceLast: getChangeSinceLast(
        energy.trendAmount,
        energy.trendDirection
      ),
      descriptionText: averageScoreDescriptionText,
      timeInterval,
      dotColor: energy.score ? getOutOfFiveColor(energy.score) : null,
      popoverText: `In the ${WEEKLY_CHECK_IN_FEATURE_NAME}, Coach Bo asks members how they’re feeling. Their answers are scored by AI on a scale of 1 (lowest) to 5 (highest). This is the average of all mood scores from the selected time frame.`,
    },
    {
      title: `${titlePrefix} Progress Score`,
      mainValue: progress.score,
      changeSinceLast: getChangeSinceLast(
        progress.trendAmount,
        progress.trendDirection
      ),
      descriptionText: averageScoreDescriptionText,
      timeInterval,
      dotColor: progress.score ? getOutOfFiveColor(progress.score) : null,
      popoverText: `In the ${WEEKLY_CHECK_IN_FEATURE_NAME}, Coach Bo asks members what they’ve been working on and their progress each check-in. Their answers are scored by AI on a scale of 1 (no progress) to 5 (great progress). This is the average of all progress scores from the selected time frame.`,
    },
    {
      title: `${titlePrefix} Readiness Score`,
      mainValue: readiness.score,
      changeSinceLast: getChangeSinceLast(
        readiness.trendAmount,
        readiness.trendDirection
      ),
      descriptionText: averageScoreDescriptionText,
      timeInterval,
      dotColor: readiness.score ? getOutOfFiveColor(readiness.score) : null,
      popoverText: `In the ${WEEKLY_CHECK_IN_FEATURE_NAME}, Coach Bo asks members what support would be most helpful for the following week. Their answers are scored by AI on a scale of 1 (major support needed) to 5 (no support needed).  This is the average of all readiness scores from the selected time frame.`,
    }
  );

  return activityMonitorBoxProps;
};

export const isWeeklyCheckInSettingLocked = (
  weeklyCheckInCompanySettings: WeeklyCheckInCompanySettings | null
) => {
  return (
    !weeklyCheckInCompanySettings?.hasWeeklyCheckInsEnabled || // if the company setting is disabled, the setting is locked
    !!weeklyCheckInCompanySettings?.hasWeeklyCheckInsEnabledForEntireCompany // if the company setting is enabled for the entire company, the setting is locked
  );
};

export const isDepartmentWeeklyCheckInSettingLocked = (
  weeklyCheckInCompanySettings: WeeklyCheckInCompanySettings | null,
  departmentInfo?: Department | null,
  isUserAdmin?: BoolLike,
  usersById?: Record<number, UserInfo>
) => {
  return (
    isWeeklyCheckInSettingLocked(weeklyCheckInCompanySettings) ||
    !checkIfUserIsAbleToTurnOffWeeklyCheckIn(
      isUserAdmin,
      departmentInfo?.weeklyCheckInsEnabledByUserAccountId,
      usersById
    )
  );
};

export const getTeamWeeklyCheckSettingLocked = (
  weeklyCheckInCompanySettings: WeeklyCheckInCompanySettings | null,
  departmentInfo?: Department | null,
  teamInfo?: Team | null,
  isUserAdmin?: BoolLike,
  usersById?: Record<number, UserInfo>
) => {
  return (
    isWeeklyCheckInSettingLocked(weeklyCheckInCompanySettings) ||
    !!departmentInfo?.hasCoachBoWeeklyCheckInsEnabled ||
    !checkIfUserIsAbleToTurnOffWeeklyCheckIn(
      isUserAdmin,
      teamInfo?.weeklyCheckInsEnabledByUserAccountId,
      usersById
    )
  );
};

export const checkIfUserIsAbleToTurnOffWeeklyCheckIn = (
  isCurrUserAdmin?: BoolLike,
  weeklyCheckInsEnabledByUserAccountId?: number | null,
  usersById?: Record<number, UserInfo>
) => {
  const wasSettingTurnedOnByAdmin =
    weeklyCheckInsEnabledByUserAccountId &&
    usersById?.[weeklyCheckInsEnabledByUserAccountId]?.tmgRoleId === 1;
  return isCurrUserAdmin || !wasSettingTurnedOnByAdmin;
};

export const getTotalPeopleInSelectedEntitiesFromCustomList = (
  weeklyCheckInCustomList: WeeklyCheckInCustomList | null,
  allUsersSubordinates: Record<number, UserSubordinates>,
  teamsById: Record<number, Team>,
  departmentsById: Record<number, Department>
) => {
  if (!weeklyCheckInCustomList) return 0;

  const { managers, teams, departments } = weeklyCheckInCustomList;
  const userAccountIds: number[] = [];
  managers?.forEach((manager) => {
    const subordinates = allUsersSubordinates[manager.userAccountId];
    if (manager.entireReportingStructure) {
      userAccountIds.push(...(subordinates?.allSubordinates ?? []));
    } else {
      userAccountIds.push(...(subordinates?.directReports ?? []));
    }
  });

  departments?.forEach((departmentId) => {
    const department = departmentsById[departmentId];
    if (department?.teamMembers) {
      userAccountIds.push(...department.teamMembers);
    }
  });
  teams?.forEach((teamId) => {
    const team = teamsById[teamId];
    if (team?.teamMemberIds) {
      userAccountIds.push(...team.teamMemberIds);
    }
  });
  return new Set(userAccountIds).size;
};

export const getSearchInput = (
  placeholder: string,
  displayedInput: string,
  setDisplayedInput: (input: string) => void
) => {
  const { selectStyles, components } = getSelectProps();

  return (
    <Form.Group>
      <Select
        placeholder={placeholder}
        isClearable={true}
        isSearchable={true}
        components={components}
        inputValue={displayedInput}
        styles={selectStyles}
        menuIsOpen={false}
        onInputChange={(e, actionMeta) => {
          if (actionMeta.action === "input-change") {
            setDisplayedInput(e);
          }
        }}
      />
    </Form.Group>
  );
};

export const getDropdownFromRecord = (
  record: Record<string, string>,
  selected: string,
  onSelect: (str: string | null) => void,
  isDisabled: boolean = false
) => {
  const recordEntries = Object.entries(record);
  return (
    <Dropdown onSelect={onSelect}>
      <Dropdown.Toggle
        id="dropdown-basic"
        className="d-flex align-items-center justify-content-between"
        style={{ width: "200px" }}
        variant="light"
        disabled={isDisabled}
      >
        {record[selected]}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {recordEntries.map(([key, value], idx) => (
          <React.Fragment key={key}>
            <Dropdown.Item eventKey={key}>{value}</Dropdown.Item>
            {recordEntries.length - 1 !== idx && <Dropdown.Divider />}
          </React.Fragment>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export const getDateCell = (startDate: string, endDate?: string) => {
  const startDateObj = new Date(startDate);
  const startDisplayValue = startDateObj.toLocaleDateString("en-US", {
    month: endDate ? "short" : "long",
    day: "numeric",
  });

  // Guard clause for single date
  if (!endDate) {
    const timeString = startDateObj.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
    });
    return {
      sortValue: startDateObj.getTime(),
      displayValue: (
        <div>
          <p>{startDisplayValue}</p>
          <p className="small-grey-text">{timeString}</p>
        </div>
      ),
    };
  }

  // If endDate is provided, format as date range
  const endDateObj = new Date(endDate);
  const endDateString = endDateObj.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const endYearString = endDateObj.getFullYear();
  return {
    sortValue: startDateObj.getTime(),
    displayValue: (
      <p>
        {startDisplayValue} - {endDateString}, {endYearString}
      </p>
    ),
  };
};

export const getUserInfo = (
  usersById: Record<number, UserInfo>,
  userAccountId?: number | null,
  className?: string
) => {
  const user = userAccountId && usersById[userAccountId];
  if (!user) {
    return null;
  }
  const { firstName, lastName, jobTitle } = user;
  const fullName = `${firstName} ${lastName}`.trim();
  return (
    <div className={`row-gap-8px ${className}`}>
      <AvatarCircle userAccountId={userAccountId} />
      <div>
        <p className="fw-bold">{fullName}</p>
        <p className="small-grey-text">{jobTitle}</p>
      </div>
    </div>
  );
};

export const getScoreSpan = (
  label: string,
  score?: number | null,
  options?: {
    isInverted?: boolean;
    prependedScoreText?: string;
    appendScoreText?: string;
  }
) => {
  const color = getOutOfFiveColor(score ?? 0, options?.isInverted);
  let scoreValue = "";
  if (score === null || score === undefined) {
    scoreValue = "N/A";
  } else if (Number.isInteger(score)) {
    scoreValue = score.toString();
  } else {
    scoreValue = score.toFixed(1);
  }

  return (
    <div className={`out-of-5-span-tag ${color}`}>
      <p>
        {label}: {options?.prependedScoreText ?? ""}
        {scoreValue}
        {options?.appendScoreText ?? ""}
      </p>
    </div>
  );
};

export const getUpdateWeeklyCheckInCompanySettingsToastMessage = (
  payload: Partial<WeeklyCheckInCompanySettings>
) => {
  if (
    payload.hasWeeklyCheckInsEnabled === false ||
    payload.hasWeeklyCheckInsEnabled === 0
  ) {
    return `${WEEKLY_CHECK_IN_FEATURE_NAME}s Disabled Successfully`;
  }

  if (payload.hasWeeklyCheckInsEnabledForEntireCompany) {
    return `${WEEKLY_CHECK_IN_FEATURE_NAME}s Enabled For Entire Company`;
  }
  if (payload.hasWeeklyCheckInExecutiveReportEnabledForAllAdmins) {
    return `${WEEKLY_CHECK_IN_FEATURE_NAME}s Executive Check-in Report Enabled For All Admins`;
  }

  return null;
};

export const getWeeklyCheckInSettingDisabledDueToCompanySettingBanner = (
  isUserAdmin: boolean
) => {
  if (!isUserAdmin) {
    return (
      <div className="gold warning-banner">
        <p>
          <FontAwesomeIcon icon={["far", "lock-keyhole"]} className="me-2" />
          {WEEKLY_CHECK_IN_FEATURE_NAME}s are not enabled for this organization.
          Please reach out to an admin to enable this feature.
        </p>
      </div>
    );
  }

  return (
    <div className="gold warning-banner">
      <div className="row-gap-12px justify-content-between align-items-center w-100">
        <p>
          <FontAwesomeIcon icon={["far", "lock-keyhole"]} className="me-2" />
          {WEEKLY_CHECK_IN_FEATURE_NAME}s aren’t currently enabled for this
          organization. You can enable {WEEKLY_CHECK_IN_FEATURE_NAME}s in
          organization settings.
        </p>
        <p className="nowrap">
          <Link to="/AdminConsole/Organization%20Settings/Check-Ins">
            Go to organization settings{" "}
            <FontAwesomeIcon icon="arrow-right" className="ms-2" />
          </Link>
        </p>
      </div>
    </div>
  );
};

export const getWeeklyCheckInSettingDisabledDueToCompanyWideSettingBanner = (
  isUserAdmin: boolean,
  type?: "department" | "team"
) => {
  const additionalInfo = isUserAdmin
    ? `You can modify ${WEEKLY_CHECK_IN_FEATURE_NAME}s settings in organization settings.`
    : "Please reach out to an admin to change these settings.";

  return (
    <div className="gold warning-banner">
      <div className="row-gap-12px justify-content-between align-items-center w-100">
        <p>
          <FontAwesomeIcon icon={["far", "lock-keyhole"]} className="me-2" />
          {WEEKLY_CHECK_IN_FEATURE_NAME}s have been <b>enabled</b> for everyone
          in this organization{type ? `, including this ${type}` : ""}.{" "}
          {additionalInfo}
        </p>
        {!!isUserAdmin && (
          <p className="nowrap">
            <Link to="/AdminConsole/Organization%20Settings/Check-Ins">
              Go to organization settings{" "}
              <FontAwesomeIcon icon="arrow-right" className="ms-2" />
            </Link>
          </p>
        )}
      </div>
    </div>
  );
};

export const getEntitiesToUpdateForCustomList = (
  existingCustomList: WeeklyCheckInCustomList | null,
  updatedCustomList: Partial<WeeklyCheckInCustomList>,
  entity: "departments" | "teams"
): { remove: number[]; add: number[] } => {
  const entitiesToUpdate: { remove: number[]; add: number[] } = {
    remove: [],
    add: [],
  };

  if (!existingCustomList || !updatedCustomList) {
    return entitiesToUpdate;
  }

  const existingEntities = existingCustomList[entity];
  const updatedEntities = updatedCustomList[entity];

  if (existingEntities && updatedEntities) {
    const existingEntityIds = new Set(existingEntities);
    const updatedEntityIds = new Set(updatedEntities);

    // Find entities to remove
    existingEntityIds.forEach((id) => {
      if (!updatedEntityIds.has(id)) {
        entitiesToUpdate.remove.push(id);
      }
    });

    // Find entities to add
    updatedEntityIds.forEach((id) => {
      if (!existingEntityIds.has(id)) {
        entitiesToUpdate.add.push(id);
      }
    });
  }

  return entitiesToUpdate;
};

export const updateEntitiesBasedOnChangesToCustomList = <
  T extends Department | Team
>(
  entities: { [id: number]: T },
  entitiesToUpdate: { remove: number[]; add: number[] }
) => {
  const copyEntities = { ...entities };

  entitiesToUpdate.remove.forEach((id) => {
    copyEntities[id] = {
      ...copyEntities[id],
      hasCoachBoWeeklyCheckInsEnabled: 0,
      weeklyCheckInsEnabledByUserAccountId: undefined,
    };
  });

  entitiesToUpdate.add.forEach((id) => {
    copyEntities[id] = {
      ...copyEntities[id],
      hasCoachBoWeeklyCheckInsEnabled: 1,
    };
  });

  return copyEntities;
};

export const getViewWeeklyCheckInReportButton = (
  onActionClick: () => void,
  reportVisibilitySettings?: WeeklyCheckInCompanySettings["reportVisibilitySettings"],
  isUserAdmin?: boolean,
  isSelf?: boolean,
  buttonText: string = "View report(s)",
  popoverText: string = "This email report is only visible to this user and admins. Please reach out to an admin to change visibility settings."
) => {
  if (
    reportVisibilitySettings !== "individual-and-admin" ||
    isUserAdmin ||
    isSelf
  ) {
    return (
      <Button variant="secondary-blue" onClick={onActionClick}>
        {buttonText}
      </Button>
    );
  }

  return (
    <SimplePopover
      popoverContent={<p>{popoverText}</p>}
      hideTitleAndCloseButton
      trigger="hover"
    >
      <div className="row-gap-12px align-items-center">
        <FontAwesomeIcon
          icon={["far", "lock-keyhole"]}
          style={{ color: "#805100" }}
        />
        <Button variant="secondary-blue" disabled>
          {buttonText}
        </Button>
      </div>
    </SimplePopover>
  );
};

export const getWeeklyCheckInBreakDownKey = (selectedCheckInBreakdownEntity: {
  weeklyCheckInInstanceId: number;
  entityType: string;
  entityId: number;
}) =>
  `${selectedCheckInBreakdownEntity.weeklyCheckInInstanceId}_${selectedCheckInBreakdownEntity.entityType}_${selectedCheckInBreakdownEntity.entityId}`;

export const getRiskPattern = (
  isBurnoutRisk?: boolean,
  isTurnoverRisk?: boolean
):
  | "No Issues"
  | "Things Improving"
  | "Recent Challenge"
  | "May Need Support" => {
  if (isBurnoutRisk && isTurnoverRisk) {
    return "May Need Support";
  }
  if (isBurnoutRisk) {
    return "Recent Challenge";
  }
  if (isTurnoverRisk) {
    return "Things Improving";
  }
  return "No Issues";
};

// Must only be called from within a createSlice reducer — mutates Immer draft rows directly.
export const clearAlertFromRows = (
  rows: WeeklyCheckInTableRowData[] | undefined,
  alertId: number
) => {
  rows?.forEach((row) => {
    if (isWeeklyCheckInRowUser(row) && row.hrConcern?.id === alertId) {
      row.hrConcern = null;
    }
  });
};

// Must only be called from within a createSlice reducer — mutates Immer draft store directly.
export const clearAlertFromStore = (
  store: AllWeeklyCheckInTableData | undefined,
  alertId: number
) => {
  Object.values(store ?? {}).forEach((byEntity) =>
    Object.values(byEntity ?? {}).forEach((rows) =>
      clearAlertFromRows(rows, alertId)
    )
  );
};
